import { FiImage, FiCheck, FiSearch, FiLoader, FiUploadCloud, FiFileText, FiVideo, FiX } from 'react-icons/fi';
import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../supabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import VideoThumbnail from '../common/VideoThumbnail';


const MediaPickerModal = ({ isOpen, onClose, onSelect }) => {
  const { activeWorkspace } = useWorkspace();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (isOpen && activeWorkspace) {
      fetchAssets();
      setSelectedAsset(null);
      setSearchQuery('');
    }
  }, [isOpen, activeWorkspace]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('ad_creatives')
        .select('*')
        .eq('workspace_id', activeWorkspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAssets(data || []);
    } catch (err) {
      console.error('Error fetching content:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cropImageToSquare = (file) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            // Auto crop to 1:1 square to satisfy Instagram and general ad platforms
            const size = Math.min(img.width, img.height);
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            const x = (img.width - size) / 2;
            const y = (img.height - size) / 2;
            ctx.drawImage(img, x, y, size, size, 0, 0, size, size);

            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(new File([blob], file.name, { type: file.type }));
                } else {
                    reject(new Error('Canvas toBlob failed'));
                }
            }, file.type, 0.95);
        };
        img.onerror = () => reject(new Error('Failed to load image for cropping'));
        img.src = URL.createObjectURL(file);
    });
  };

  const handleUpload = async (files) => {
    if (!activeWorkspace || !files || files.length === 0) return;

    setUploading(true);
    const newAssets = [];

    try {
      for (let i = 0; i < files.length; i++) {
        let file = files[i];
        let fileType = 'image';
        if (file.type.startsWith('video')) fileType = 'video';
        else if (file.type.includes('pdf') || file.type.includes('document') || file.name.endsWith('.txt')) fileType = 'document';

        // Auto-crop images to square (1:1) to prevent Instagram aspect ratio errors
        if (fileType === 'image' && (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp')) {
            try {
                file = await cropImageToSquare(file);
            } catch (cropErr) {
                console.warn("Failed to auto-crop image, continuing with original:", cropErr);
            }
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${activeWorkspace.id}/${fileName}`;

        const arrayBuffer = await file.arrayBuffer();

        const { error: uploadError } = await supabase.storage
            .from('ad-creatives')
            .upload(filePath, arrayBuffer, {
                contentType: file.type,
                upsert: true
            });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage.from('ad-creatives').getPublicUrl(filePath);

        const newAsset = {
            workspace_id: activeWorkspace.id,
            name: file.name,
            type: fileType,
            format: 'custom',
            status: 'active',
            performance: 'new',
            platforms: [],
            image_url: publicUrl
        };

        const { data: insertedData, error: dbError } = await supabase.from('ad_creatives').insert([newAsset]).select();
        if (dbError) throw dbError;

        if (insertedData && insertedData[0]) {
             newAssets.push(insertedData[0]);
             // Auto-select the uploaded asset
             setSelectedAsset(insertedData[0]);
        }
      }

      setAssets(prev => [...newAssets, ...prev]);
    } catch (error) {
      console.error('Upload failed:', error);
      setError('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const filteredAssets = assets.filter(asset =>
    asset.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderThumbnail = (asset) => {
    if (asset.type === 'document') {
       return <SafeIcon icon={FiFileText} className="text-charcoal-400 text-3xl" />;
    }
    if (asset.type === 'video' && asset.image_url) {
        return <VideoThumbnail src={asset.image_url} className="w-full h-full object-cover" />;
    }
    if (asset.image_url) {
        return <img src={asset.image_url} alt={asset.name} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = encodeURI(asset.image_url); }} />;
    }
    return <SafeIcon icon={asset.type === 'video' ? FiVideo : FiImage} className="text-charcoal-400 text-3xl" />;
  };

  const handleConfirm = () => {
    if (selectedAsset) {
        onSelect(selectedAsset);
        onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-[100] p-4 sm:p-6 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] flex flex-col overflow-hidden animate-fade-in my-auto">

        {/* Header */}
        <div className="p-4 border-b border-charcoal-100 flex justify-between items-center bg-charcoal-50 shrink-0">
          <h3 className="font-bold text-lg text-charcoal-900">Select Media</h3>
          <button onClick={onClose} aria-label="Close media picker" className="p-2 hover:bg-charcoal-200 rounded-full text-charcoal-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="p-4 border-b border-charcoal-100 flex items-center gap-4 shrink-0">
            <div className="relative flex-1">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
                <input
                    type="text"
                    placeholder="Search media..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-charcoal-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-charcoal-900"
                />
            </div>
            <label className="flex items-center gap-2 px-4 py-2 bg-white border border-charcoal-200 rounded-lg cursor-pointer hover:bg-charcoal-50 text-charcoal-700 font-medium text-sm transition-colors">
                <SafeIcon icon={FiUploadCloud} />
                <span>Upload</span>
                <input
                    type="file"
                    className="hidden"
                    onChange={(e) => handleUpload(e.target.files)}
                    accept="image/*,video/*,audio/*,.mp3,.mp4"
                    disabled={uploading}
                />
            </label>
        </div>

        {/* Content */}
        <div
            className="flex-1 min-h-0 overflow-y-auto p-6 bg-charcoal-50 relative"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
        >
            {dragActive && (
                <div className="absolute inset-0 z-10 bg-charcoal-900/10 border-4 border-dashed border-charcoal-400 m-4 rounded-xl flex items-center justify-center backdrop-blur-sm">
                     <div className="text-center p-8 bg-white rounded-xl shadow-xl">
                        <SafeIcon icon={FiUploadCloud} className="text-4xl text-charcoal-400 mx-auto mb-2" />
                        <h3 className="text-lg font-bold text-charcoal-900">Drop files to upload</h3>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center h-full">
                    <SafeIcon icon={FiLoader} className="animate-spin text-4xl text-charcoal-400" />
                </div>
            ) : error ? (
                <div className="text-center text-red-500 py-10">{error}</div>
            ) : filteredAssets.length === 0 ? (
                <div className="text-center text-charcoal-400 py-20">
                    <SafeIcon icon={FiImage} className="text-5xl mx-auto mb-4 opacity-50" />
                    <p>No media found. Upload some files!</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {filteredAssets.map(asset => (
                        <div
                            key={asset.id}
                            onClick={() => setSelectedAsset(asset)}
                            className={`group relative aspect-square bg-white rounded-lg border-2 cursor-pointer overflow-hidden transition-all ${
                                selectedAsset?.id === asset.id
                                ? 'border-charcoal-900 ring-2 ring-charcoal-900 ring-offset-2'
                                : 'border-charcoal-200 hover:border-charcoal-400'
                            }`}
                        >
                            <div className="w-full h-full flex items-center justify-center bg-charcoal-100">
                                {renderThumbnail(asset)}
                            </div>

                            {selectedAsset?.id === asset.id && (
                                <div className="absolute top-2 right-2 bg-charcoal-900 text-white rounded-full p-1 shadow-md">
                                    <SafeIcon icon={FiCheck} className="text-xs" />
                                </div>
                            )}

                            <div className="absolute bottom-0 left-0 right-0 bg-white/90 p-2 text-xs truncate font-medium text-charcoal-700 opacity-0 group-hover:opacity-100 transition-opacity">
                                {asset.name}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-white border-t border-charcoal-100 flex justify-end gap-3 shrink-0">
             <button
                onClick={onClose}
                className="px-4 py-2 text-charcoal-600 hover:bg-charcoal-100 rounded-lg transition-colors font-medium text-sm"
             >
                Cancel
             </button>
             <button
                onClick={handleConfirm}
                disabled={!selectedAsset}
                className="px-6 py-2 bg-charcoal-900 text-white rounded-lg hover:bg-charcoal-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center gap-2"
             >
                <SafeIcon icon={FiCheck} />
                Select Media
             </button>
        </div>

      </div>
    </div>
  );
};

export default MediaPickerModal;
